import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  GlobalWebviewAuthService,
  WebviewAuthState,
} from '../../services/global-webview-auth.service';

@Component({
  selector: 'app-global-webview-auth-modal',
  template: `
    <div class="webview-auth-overlay" *ngIf="authState.isVisible">
      <div class="auth-modal">
        <div class="modal-header">
          <div class="warning-icon">🚫</div>
          <h2>Sign In Not Available</h2>
          <p class="subtitle">
            You're browsing in <strong>{{ authState.webviewType }}</strong>
          </p>
        </div>

        <div class="modal-body">
          <div class="problem-explanation">
            <p>
              <strong>Google sign-in is blocked</strong> in this browser for security reasons. To
              continue, you need to open this page in your device's main browser.
            </p>
          </div>

          <div class="instructions-box">
            <h4>📱 How to continue:</h4>
            <ol class="step-list">
              <li>Copy this page's link using the button below</li>
              <li>{{ getInstructions() }}</li>
              <li>Paste and open the link in your browser</li>
            </ol>
          </div>

          <div class="action-buttons">
            <button (click)="copyLink()" class="btn-primary">📋 Copy Link to Browser</button>
          </div>

          <div class="alternative-note">
            <p class="small-text">
              💡 <strong>Quick tip:</strong> After copying, you can paste the link in any browser
              app on your device to sign in with Google.
            </p>
          </div>
        </div>

        <button (click)="closeModal()" class="close-button" type="button">✕</button>
      </div>
    </div>
  `,
  styles: [
    `
      .webview-auth-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.9);
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        animation: fadeIn 0.3s ease;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .auth-modal {
        background: white;
        border-radius: 16px;
        max-width: 450px;
        width: 100%;
        position: relative;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.3s ease;
      }

      @keyframes slideUp {
        from {
          transform: translateY(50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .modal-header {
        text-align: center;
        padding: 2rem 2rem 1rem 2rem;
        border-bottom: 1px solid #e9ecef;
      }

      .warning-icon {
        font-size: 3.5rem;
        margin-bottom: 1rem;
      }

      h2 {
        margin: 0 0 0.5rem 0;
        color: #dc3545;
        font-size: 1.5rem;
        font-weight: 600;
      }

      .subtitle {
        margin: 0;
        color: #6c757d;
        font-size: 1rem;
      }

      .modal-body {
        padding: 1.5rem 2rem 2rem 2rem;
      }

      .problem-explanation {
        margin-bottom: 1.5rem;
      }

      .problem-explanation p {
        margin: 0;
        color: #495057;
        line-height: 1.5;
      }

      .instructions-box {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 1.25rem;
        margin-bottom: 1.5rem;
      }

      .instructions-box h4 {
        margin: 0 0 0.75rem 0;
        color: #495057;
        font-size: 1rem;
        font-weight: 600;
      }

      .step-list {
        margin: 0;
        padding-left: 1.25rem;
        color: #495057;
      }

      .step-list li {
        margin-bottom: 0.5rem;
        line-height: 1.5;
      }

      .step-list li:last-child {
        margin-bottom: 0;
      }

      .action-buttons {
        display: flex;
        gap: 0.75rem;
        margin-bottom: 1rem;
        justify-content: center;
      }

      .btn-primary {
        padding: 1rem 2rem;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        background: #28a745;
        color: white;
        min-width: 200px;
      }

      .btn-primary:hover {
        background: #218838;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
      }

      .alternative-note {
        text-align: center;
        padding-top: 1rem;
        border-top: 1px solid #e9ecef;
      }

      .small-text {
        margin: 0;
        font-size: 0.875rem;
        color: #6c757d;
        font-style: italic;
      }

      .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: transparent;
        border: none;
        font-size: 1.5rem;
        color: #6c757d;
        cursor: pointer;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.2s ease;
      }

      .close-button:hover {
        background: #f8f9fa;
        color: #495057;
      }

      @media (max-width: 480px) {
        .webview-auth-overlay {
          padding: 0.5rem;
        }

        .modal-header,
        .modal-body {
          padding: 1.5rem 1.5rem;
        }

        .btn-primary {
          min-width: auto;
          width: 100%;
        }
      }
    `,
  ],
})
export class GlobalWebviewAuthModalComponent implements OnInit, OnDestroy {
  authState: WebviewAuthState = { isVisible: false };
  private subscription?: Subscription;

  constructor(private globalWebviewAuthService: GlobalWebviewAuthService) {}

  ngOnInit(): void {
    this.subscription = this.globalWebviewAuthService.webviewAuthState.subscribe(
      state => (this.authState = state),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getInstructions(): string {
    return this.globalWebviewAuthService.getOpenInBrowserInstructions();
  }

  copyLink(): void {
    this.globalWebviewAuthService.copyToClipboard(window.location.href);
  }

  closeModal(): void {
    this.globalWebviewAuthService.hideWebviewAuthModal();
  }
}
